import { call, select } from 'redux-saga/effects';
import { eventBus } from 'utils/eventBus';
import { selectCustomTileById } from 'features/layout/features/config/configSlice';
import { CustomTileData } from 'features/layout/features/config/types';
import { sendMessageToCustomTileSchema } from 'features/sdk/utils/schemas';
import { fromZodError } from 'zod-validation-error';
import * as z from 'zod';

export type SendMessageToCustomTileOptions = z.infer<typeof sendMessageToCustomTileSchema>;

export function* onSendMessageToCustomTileSaga(options?: SendMessageToCustomTileOptions) {
  if (!options) {
    yield call(eventBus.error, {
      name: 'custom-tile',
      message: 'No options were provided for sending a message to a custom tile.',
    });
    return;
  }

  const validationResult = sendMessageToCustomTileSchema.safeParse(options);

  if (!validationResult.success) {
    const validationError = fromZodError(validationResult.error);
    yield call(eventBus.error, {
      name: 'custom-tile',
      message: validationError.toString(),
    });
    return;
  }

  const { name: id, event, origin, data } = options;

  const customTile: CustomTileData = yield select(selectCustomTileById, id);
  if (!customTile) {
    yield call(eventBus.error, {
      name: 'custom-tile',
      message: `Custom tile not found. No tile exists in the state for the provided ID (${id}).`,
    });
    return;
  }

  const customTileContainer: HTMLElement | null = document.getElementById(`custom-tile-${id}`);
  if (!customTileContainer) {
    yield call(eventBus.error, {
      name: 'custom-tile',
      message: `Iframe container element not found. No iframe container exists in the DOM for the custom tile (${id}).`,
    });

    return;
  }

  const customTileIframe: HTMLIFrameElement | null = customTileContainer.querySelector('iframe');
  if (!customTileIframe) {
    yield call(eventBus.error, {
      name: 'custom-tile',
      message: `Iframe not found. No iframe exists in the DOM for the custom tile (${id}).`,
    });

    return;
  }

  customTileIframe.contentWindow?.postMessage(
    {
      type: event ?? 'custom-tile',
      data,
    },
    origin ?? '*'
  );
}
